import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ConsentType,
  getChatConsentContent,
  getRobotConsentContent,
  saveRobotConsentContent,
} from '@mygpt/app/consent';

import { TiptapRichEditor, TiptapRichEditorRef } from '@/components/TiptapRichEditor';

interface Props {
  botId: string;
  /** chat 与 realtime 的自定义规约各自独立一份 */
  consentType: ConsentType;
}

/**
 * Bot 级自定义利用规约编辑器（富文本编辑,内部以 Markdown 存取,后端契约不变）
 * - 保存后对应场景（chat / realtime）的同意弹窗优先展示该内容
 * - chat 与 realtime 的自定义规约各自独立配置
 * - 内容为空表示未设置，前台回退到内置模版
 * - 版本策略：内容变更即视为新 version（后端按内容 sha256 截断生成），版本变更后用户需重新同意
 */
export const ConsentContentEditor = ({ botId, consentType }: Props) => {
  const { t, i18n } = useTranslation();
  const editorRef = useRef<TiptapRichEditorRef>(null);
  const builtinTemplate = useMemo(() => getChatConsentContent(i18n.language), [i18n.language]);
  const [content, setContent] = useState(builtinTemplate);
  const [saving, setSaving] = useState(false);

  // 加载已保存的自定义规约
  useEffect(() => {
    if (!botId) return;
    let cancelled = false;
    getRobotConsentContent({ botId, consentType })
      .then((record) => {
        if (cancelled) return;
        const nextContent = record?.content || builtinTemplate;
        setContent(nextContent);
        editorRef.current?.setValue(nextContent);
      })
      .catch((error) => {
        // 接口未就绪 / 失败：按未设置处理
        console.warn('加载自定义规约失败:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [botId, builtinTemplate, consentType]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveRobotConsentContent({ botId, consentType, content });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存自定义规约失败:', error);
      showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  /** 一键重置为默认模版：清除自定义内容并保存,前台回退内置模版 */
  const handleReset = () => {
    modals.openConfirmModal({
      title: t('consent.editor.reset'),
      centered: true,
      children: <Text size="sm">{t('consent.editor.reset_confirm')}</Text>,
      labels: { confirm: t('confirm'), cancel: t('cancel') },
      onConfirm: async () => {
        try {
          setSaving(true);
          await saveRobotConsentContent({ botId, consentType, content: '' });
          setContent(builtinTemplate);
          editorRef.current?.setValue(builtinTemplate);
          showNotification({ message: t('save_success'), color: 'green' });
        } catch (error) {
          console.error('重置自定义规约失败:', error);
          showNotification({ message: t('settings.realtime.save_error'), color: 'red' });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Box>
      <Stack gap="sm">
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            {t('consent.editor.custom_title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('consent.editor.custom_desc')}
          </Text>
        </Stack>
        <TiptapRichEditor
          ref={editorRef}
          autoExpand
          defaultValue={content}
          contentMinHeight={360}
          placeholder={t('consent.editor.content_placeholder')}
          onChange={setContent}
        />
        <Group justify="space-between">
          <Button size="sm" variant="outline" color="gray" disabled={saving} onClick={handleReset}>
            {t('consent.editor.reset')}
          </Button>
          <Button size="sm" loading={saving} onClick={handleSave}>
            {t('consent.editor.save')}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};
